import React, { useState, useEffect, useMemo, useCallback } from "react";
import { FaRocket, FaCheckCircle, FaTimesCircle } from "react-icons/fa";
import { useParams, useNavigate } from "react-router-dom";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const NotificationCard = ({ notification, onDelete }) => {
  const { type, message, projectId, timestamp, read, id, actor } = notification;
  const { username } = useParams();
  const navigate = useNavigate();
  const [isHovered, setIsHovered] = useState(false);
  const [actorFirstName, setActorFirstName] = useState(null);
  const [actorProfileImage, setActorProfileImage] = useState(null); 

  // Fetch firstName based on actor (email)
  useEffect(() => {
    const fetchActorName = async () => {
      if (!actor || !actor.includes("@")) return;

      try {
        const res = await fetch(`${BACKEND_URL}/user/${encodeURIComponent(actor)}`);
        if (!res.ok) return;
        const data = await res.json();
        setActorFirstName(data.firstName || "Someone");
        setActorProfileImage(data.profileImage || "https://via.placeholder.com/50");
      } catch (err) {
        console.error("❌ Error fetching actor's name & image:", err);
      }
    };

    if (type === "new_message" || type === "application_submitted" || type === "comment_by") fetchActorName();
  }, [actor, type]);

  const formattedTime = useMemo(() => {
    if (!timestamp) return "Just now";
    let date;
    if (typeof timestamp === "object" && timestamp._seconds !== undefined) {
      date = new Date(timestamp._seconds * 1000);
    } else if (typeof timestamp === "number") {
      date = timestamp < 1e10 ? new Date(timestamp * 1000) : new Date(timestamp);
    } else {
      date = new Date(timestamp);
    }
    return isNaN(date.getTime())
      ? "Just now"
      : date.toLocaleString("en-US", {
          month: "short",
          day: "numeric",
          hour: "2-digit",
          minute: "2-digit",
        });
  }, [timestamp]);

  const handleNotificationClick = useCallback(async () => {
    if (type === "project_created" && projectId) {
      navigate(`/projects/${projectId}/admin-view`);
    } else if (type === "ProjectApproval" && projectId) {
      navigate(`/projects/${projectId}/collaborate`);
    } else if (type === "application_submitted" && projectId) {
      navigate(`/projects/${projectId}/admin-view`);
    }

    try {
      const token = localStorage.getItem("token");
      if (!token) return;

      const response = await fetch(`${BACKEND_URL}/notifications/${id}`, {
        method: "DELETE",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
      });

      if (response.ok && onDelete) {
        onDelete(id);
      }
    } catch (error) {
      console.error("❌ Error deleting notification:", error);
    }
  }, [type, projectId, id, navigate, onDelete]);

  const getIcon = () => {
    if ((type === "new_message" || type === "application_submitted" || type === "comment_by" ) && actorProfileImage) {
        return (
            <img
                src={actorProfileImage}
                alt="Sender"
                className="w-7 h-7 rounded-full object-cover border border-gray-300"
            />
        );
     }

    if (type === "project_created") return <FaRocket size={16} className="text-blue-600" />;
    if (type === "ProjectApproval") return <FaCheckCircle size={16} className="text-green-600" />;
    if (type === "ProjectRejection") return <FaTimesCircle size={16} className="text-red-500" />;

    return <FaRocket size={16} className="text-gray-500" />;
  };


  // 📩 Handle message text rewrite
  const getDisplayMessage = () => {
    if (type === "new_message" && actorFirstName) {
      return `You received a new message on Frontera from ${actorFirstName}.`;
    }
    return message;
  };

  return (
    <div
      className={`w-full flex items-start px-4 py-3 transition-all duration-150 cursor-pointer ${
        read ? "bg-white" : "bg-gray-50"
      } hover:bg-gray-100`}
      onClick={handleNotificationClick}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      role="button"
      tabIndex={0}
      onKeyDown={(e) => {
        if (e.key === "Enter") handleNotificationClick();
      }}
    >
      {/* Icon / Profile Image */}
    <div className="mt-1 mr-3">
        {type === "new_message" && actorProfileImage ? (
            <img
                src={actorProfileImage}
                alt="Sender"
                className="w-6 h-6 rounded-full object-cover"
            />
        ) : (
            getIcon()
        )}
    </div>


      {/* Content */}
      <div className="flex-1 min-w-0">
        <p className="text-sm text-gray-800 leading-tight">{getDisplayMessage()}</p>
        <span className="text-xs text-gray-500">{formattedTime}</span>
      </div>

      {/* Delete button */}
      {isHovered && (
        <button
          onClick={(e) => {
            e.stopPropagation();
            if (onDelete) onDelete(id);
          }}
          className="text-xs text-gray-400 hover:text-red-500 ml-4"
        >
          ✕
        </button>
      )}
    </div>
  );
};

export default NotificationCard;
